var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Box } from '../Layout';
import Typography from '../Typography';
import React from 'react';
import { Fade, Slide } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Modal as BaseModal } from '@mui/base/Modal/Modal';
import designTokens from '@customink/design-tokens/build/ci_light/js/designTokens';
import IconButton from '../IconButton';
import { XCloseIcon } from '../../icons';
import { useMobileBreakpoint } from '../../utils';
var widthMap = {
    sm: '420px',
    md: '600px',
    lg: '900px',
    full: '100%',
};
export var useVariant = function (desktopVariant, mobileVariant) {
    var isMobile = useMobileBreakpoint();
    if (desktopVariant === 'fullScreen' && !isMobile)
        return 'fullScreen';
    if (mobileVariant === 'fullScreen' && isMobile)
        return 'fullScreen';
    if (mobileVariant === 'bottomSheet' && isMobile)
        return 'bottomSheet';
    return isMobile ? 'defaultMobile' : 'defaultDesktop';
};
export var useDialogStylesWarning = function (_a) {
    var surfaceSx = _a.surfaceSx;
    if (surfaceSx) {
        var paddingKeys = Object.keys(surfaceSx).filter(function (key) {
            return key.includes('padding');
        });
        var pKeys = Object.keys(surfaceSx).filter(function (key) {
            return ['p', 'px', 'py'].includes(key);
        });
        if (paddingKeys.length > 0 || pKeys.length > 0) {
            console.warn("Pigment Dialog: The surfaceSx prop should not include any padding changes. Please use the padding prop instead.");
        }
    }
};
export var DialogTitle = function (_a) {
    var hideCloseButton = _a.hideCloseButton, padding = _a.padding, onClose = _a.onClose, title = _a.title, titleSx = _a.titleSx, TitleComponent = _a.titleComponent;
    return (React.createElement(Box, null,
        React.createElement(StyledCloseButton, { onClose: onClose, hideCloseButton: hideCloseButton, padding: padding }),
        title && TitleComponent === 'header' ? (React.createElement(Typography, { variant: "h5", component: "h2", fontWeight: 700, fontSize: 20, lineHeight: 1.25, paddingBottom: "8px", paddingLeft: padding.paddingLeft, paddingRight: padding.paddingRight, sx: titleSx }, title)) : (React.createElement(TitleComponent, null, title))));
};
export var Transition = React.forwardRef(function (_a, ref) {
    var variant = _a.variant, props = __rest(_a, ["variant"]);
    if (variant === 'fullScreen' || variant === 'bottomSheet') {
        return React.createElement(Slide, __assign({ direction: "up", ref: ref }, props));
    }
    else
        return React.createElement(Fade, __assign({ ref: ref }, props));
});
export var Modal = styled(BaseModal)(function () { return ({
    position: 'fixed',
    zIndex: 1300,
    inset: 0,
    right: 0,
    bottom: 0,
    top: 0,
    left: 0,
}); });
export var DialogContainer = styled(Box, {
    shouldForwardProp: function (key) {
        return !['variant', 'scroll'].includes(key);
    },
})(function (_a) {
    var variant = _a.variant, scroll = _a.scroll;
    return __assign(__assign(__assign({ boxSizing: 'border-box', maxWidth: '100%', maxHeight: '100%', width: '100%', height: '100%', outline: 0 }, (scroll === 'body' && variant !== 'bottomSheet'
        ? {
            display: 'block',
            textAlign: 'center',
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': {
                display: 'none', // Chromium
            },
            scrollbarWidth: 'none',
            '-ms-overflow-style': 'none',
            // This pattern is inspired by mui-material Dialog. It alters the *line box* height to 100%, causing verticalAlign on children to behave as desired
            '&::after': {
                content: "''",
                display: 'inline-block',
                verticalAlign: 'middle',
                height: '100%',
                width: 0,
            },
        }
        : {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        })), (variant === 'defaultDesktop' ? { padding: '24px' } : {})), (variant === 'defaultMobile' ? { padding: '16px' } : {}));
});
export var DialogSurface = styled(Box, {
    shouldForwardProp: function (key) {
        return !['variant', 'padding', 'width', 'scroll'].includes(key);
    },
})(function (_a) {
    var variant = _a.variant, width = _a.width, padding = _a.padding, scroll = _a.scroll;
    return __assign(__assign(__assign(__assign(__assign({ position: 'relative', backgroundColor: '#fff', paddingTop: padding.paddingTop, paddingBottom: padding.paddingBottom, boxShadow: designTokens.shadow.large.value, borderRadius: '8px', boxSizing: 'border-box', maxWidth: '100%', overflowY: 'auto' }, (scroll === 'body' && variant !== 'bottomSheet'
        ? {
            verticalAlign: 'middle',
            textAlign: 'initial',
            display: 'inline-block',
        }
        : {
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
        })), (variant === 'defaultDesktop'
        ? { width: widthMap[width] }
        : {})), (variant === 'defaultMobile' ? { width: '100%' } : {})), (variant === 'bottomSheet'
        ? {
            alignSelf: 'end',
            width: '100%',
            maxHeight: '50%',
            borderRadius: '16px 16px 0 0',
            boxShadow: '0px 0px 56px 0px rgba(0, 0, 0, 0.29)',
        }
        : {})), (variant === 'fullScreen'
        ? { borderRadius: 0, height: '100%', maxHeight: '100%', width: '100%' }
        : {}));
});
var Backdrop = React.forwardRef(function (props, ref) {
    var open = props.open, other = __rest(props, ["open"]);
    return (React.createElement(Fade, { in: open },
        React.createElement("div", __assign({ ref: ref }, other))));
});
export var StyledBackdrop = styled(Backdrop)(function () { return ({
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    WebkitTapHighlightColor: 'transparent',
}); });
export var StyledCloseButton = function (_a) {
    var onClose = _a.onClose, hideCloseButton = _a.hideCloseButton, padding = _a.padding;
    if (hideCloseButton)
        return null;
    return (React.createElement(Box, { sx: {
            float: 'right',
            paddingRight: '8px',
            marginTop: "calc(-".concat(padding.paddingTop, " + 8px)"),
        } },
        React.createElement(IconButton, { size: "large", onClick: onClose, "aria-label": "Close" },
            React.createElement(XCloseIcon, { fontSize: "inherit" }))));
};
//# sourceMappingURL=DialogInternals.js.map